// The Payer choices of the Payer Enrollments card, kept apart from the component so the
// card's file exports components only (Fast Refresh).

/**
 * One Payer choice: a catalog row (a payer in one state), keyed by the row's id, with the
 * payer ID and name the enrollment stores. Labelled by name alone, as the patient app lists
 * them: the card offers only the rows of the enrollment's own State.
 */
export type PayerOption = {
  value: string
  label: string
  payerId: string
  payer: string
  state?: string | undefined
}

/** The insurance payer catalog as Payer choices, one per row, in name order. */
export function toPayerOptions(
  catalog: readonly {
    id: string
    payerId: string
    payerName: string
    state?: string | undefined
  }[],
): PayerOption[] {
  return catalog
    .map((item) => ({
      value: item.id,
      label: item.payerName,
      payerId: item.payerId,
      payer: item.payerName,
      state: item.state,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/** The choices for one State; none until a State is picked. */
export function payerOptionsFor(
  options: readonly PayerOption[],
  state: string | undefined,
): PayerOption[] {
  return state ? options.filter((o) => o.state === state) : []
}

/**
 * The choice a stored row shows: the catalog row with its payer ID and name in the
 * enrollment's State. None for a row saved before the Payer became pick-only, or a payer
 * the catalog no longer lists there.
 */
export function findPayerOption(
  options: readonly PayerOption[],
  row: { payerId?: string | undefined; payer?: string | undefined; state?: string | undefined },
): PayerOption | undefined {
  if (!row.payerId || !row.payer) return undefined
  return payerOptionsFor(options, row.state).find(
    (o) => o.payerId === row.payerId && o.payer === row.payer,
  )
}
